import React, { useState } from 'react';
import { Handle } from '@xyflow/react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import CircularProgress from '@mui/joy/CircularProgress';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';

import DataObjectIcon from '@mui/icons-material/DataObject';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import { themeColors, shadows } from '../../styles/theme';
import { NODE_CONFIG, OUTPUT_FORMAT_CONFIG } from '../../config/nodeConfig';

// Add styles for handle animations and test run keyframes
const handleStyles = `
  .modern-node-handle {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .modern-node-handle.react-flow__handle-left:hover,
  .modern-node-handle.react-flow__handle-left.react-flow__handle-connecting {
    width: 24px !important;
    height: 24px !important;
    left: -12px !important;
  }

  .modern-node-handle.react-flow__handle-right:hover,
  .modern-node-handle.react-flow__handle-right.react-flow__handle-connecting {
    width: 24px !important;
    height: 24px !important;
    right: -12px !important;
  }

  .modern-node-handle.react-flow__handle-left.react-flow__handle-valid {
    width: 28px !important;
    height: 28px !important;
    left: -14px !important;
    box-shadow: 0 0 0 6px ${themeColors.primary[500]}40 !important;
  }

  .modern-node-handle.react-flow__handle-right.react-flow__handle-valid {
    width: 28px !important;
    height: 28px !important;
    right: -14px !important;
    box-shadow: 0 0 0 6px ${themeColors.primary[500]}40 !important;
  }

  @keyframes testRunPulse {
    0%, 100% { box-shadow: 0 0 0 3px ${themeColors.primary[500]}25, ${shadows.md}; }
    50% { box-shadow: 0 0 0 5px ${themeColors.primary[500]}35, ${shadows.md}; }
  }

  @keyframes testRunFadeIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes testRunCheckPop {
    0% { transform: scale(0); opacity: 0; }
    60% { transform: scale(1.3); }
    100% { transform: scale(1); opacity: 1; }
  }
`;

export default function ModernNode({ data, isConnectable, selected, nodeType }) {
  const [outputModalOpen, setOutputModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  const config = NODE_CONFIG[nodeType];
  if (!config) return null;

  const Icon = config.icon;

  // Determine content for preview
  const contentField =
    nodeType === 'ai_node' ? 'prompt' :
    nodeType === 'code_node' ? 'code' :
    nodeType === 'template_node' ? 'template' : '';

  const content = data[contentField];
  const hasContent = content && content.length > 0;
  const previewText = content
    ? (content.length > 50 ? `${content.slice(0, 50)}...` : content)
    : null;

  // For output nodes, get format info
  const outputFormat = nodeType === 'output_node' && data.output_format
    ? OUTPUT_FORMAT_CONFIG[data.output_format]
    : null;

  // Test run overlay
  const testRunStatus = data.testRunStatus; // 'pending' | 'running' | 'completed' | 'error' | undefined
  const testRunOutput = data.testRunOutput;
  const testRunError = data.testRunError;

  // Box-shadow glow for test run status (keeps border free for hover)
  const getTestRunGlow = () => {
    switch (testRunStatus) {
      case 'running': return `0 0 0 3px ${themeColors.primary[500]}30`;
      case 'completed': return `0 0 0 3px ${themeColors.success}30`;
      case 'error': return `0 0 0 3px ${themeColors.danger}30`;
      case 'pending': return `0 0 0 3px ${themeColors.neutral[400]}25`;
      default: return null;
    }
  };

  const testGlow = getTestRunGlow();

  // Border color: always uses normal logic (selected or default), never overridden by test run
  const borderColor = selected ? config.colors.border : themeColors.neutral[200];

  // Box shadow: layer test glow on top of normal shadow
  const baseBoxShadow = selected ? `0 0 0 3px ${config.colors.border}20, ${shadows.lg}` : shadows.sm;
  const boxShadow = testGlow ? `${testGlow}, ${baseBoxShadow}` : baseBoxShadow;

  // Animation for running state
  const runningAnimation = testRunStatus === 'running' ? 'testRunPulse 2s ease-in-out infinite' : undefined;

  return (
    <>
      <style>{handleStyles}</style>
      <Box
        sx={{
          minWidth: 220,
          maxWidth: 280,
          backgroundColor: 'white',
          border: `2px solid ${borderColor}`,
          boxShadow,
          borderRadius: 2,
          overflow: 'visible',
          position: 'relative',
          zIndex: selected ? 1000 : 1,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: runningAnimation,
          '&:hover': {
            borderColor: config.colors.border,
            boxShadow: testGlow
              ? `${testGlow}, ${selected ? `0 0 0 3px ${config.colors.border}20, ${shadows.lg}` : shadows.md}`
              : selected ? `0 0 0 3px ${config.colors.border}20, ${shadows.lg}` : shadows.md,
            zIndex: 999,
          },
        }}
      >
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          backgroundColor: selected ? `${config.colors.border}08` : themeColors.neutral[25],
          borderBottom: `1px solid ${themeColors.neutral[100]}`,
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: outputFormat
              ? `${outputFormat.color}15`
              : `${config.colors.border}15`,
            flexShrink: 0,
          }}
        >
          {outputFormat && outputFormat.icon ? (
            outputFormat.icon({ sx: { fontSize: 16, color: outputFormat.color } })
          ) : (
            <Icon sx={{ fontSize: 16, color: config.colors.border }} />
          )}
        </Box>

        {/* Title */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            level="body-sm"
            sx={{
              fontWeight: 600,
              color: themeColors.neutral[900],
              fontSize: '0.8125rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {data.label || config.label}
          </Typography>
          {outputFormat && (
            <Typography
              level="body-xs"
              sx={{
                color: themeColors.neutral[600],
                fontSize: '0.6875rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {outputFormat.label}
            </Typography>
          )}
        </Box>

        {/* Base Output Indicator */}
        {data.is_base_output && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.375,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              backgroundColor: '#fef3c7',
              border: '1px solid #fde68a',
              flexShrink: 0,
            }}
          >
            <DataObjectIcon sx={{ fontSize: 11, color: '#d97706' }} />
            <Typography
              level="body-xs"
              sx={{
                fontSize: '0.5625rem',
                fontWeight: 700,
                color: '#92400e',
                letterSpacing: '0.5px',
                lineHeight: 1,
              }}
            >
              BASE
            </Typography>
          </Box>
        )}
      </Box>

      {/* Content Preview */}
      {hasContent && (
        <Box sx={{ p: 1.5, pt: 1.25 }}>
          <Typography
            level="body-xs"
            sx={{
              color: themeColors.neutral[600],
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: '0.6875rem',
              lineHeight: 1.6,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {previewText}
          </Typography>
        </Box>
      )}

      {/* Test Run Overlay */}
      {testRunStatus && (
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderTop: `1px solid ${themeColors.neutral[100]}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            animation: 'testRunFadeIn 0.3s ease-out',
          }}
        >
          {testRunStatus === 'pending' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <HourglassEmptyIcon sx={{ fontSize: 12, color: themeColors.neutral[400] }} />
              <Typography level="body-xs" sx={{ color: themeColors.neutral[500], fontSize: '0.6875rem' }}>
                Waiting...
              </Typography>
            </Box>
          )}

          {testRunStatus === 'running' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CircularProgress size="sm" sx={{ '--CircularProgress-size': '14px', '--CircularProgress-trackThickness': '2px', '--CircularProgress-progressThickness': '2px' }} />
              <Typography level="body-xs" sx={{ color: themeColors.primary[600], fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.5px' }}>
                RUNNING
              </Typography>
            </Box>
          )}

          {testRunStatus === 'completed' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <CheckCircleIcon sx={{ fontSize: 12, color: themeColors.success, animation: 'testRunCheckPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' }} />
                <Typography level="body-xs" sx={{ color: themeColors.success, fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.5px' }}>
                  COMPLETED
                </Typography>
              </Box>
              {testRunOutput && (
                <Box
                  onClick={(e) => { e.stopPropagation(); setOutputModalOpen(true); }}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 1,
                    p: 0.5,
                    mx: -0.5,
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                      backgroundColor: themeColors.neutral[50],
                    },
                  }}
                >
                  <Typography
                    level="body-xs"
                    sx={{
                      color: themeColors.neutral[600],
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      fontSize: '0.625rem',
                      lineHeight: 1.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      wordBreak: 'break-all',
                    }}
                  >
                    {testRunOutput}
                  </Typography>
                  <Typography
                    level="body-xs"
                    sx={{
                      color: themeColors.primary[500],
                      fontSize: '0.5625rem',
                      mt: 0.25,
                      fontWeight: 500,
                    }}
                  >
                    Click to expand
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {testRunStatus === 'error' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <ErrorIcon sx={{ fontSize: 12, color: themeColors.danger, animation: 'testRunCheckPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' }} />
                <Typography level="body-xs" sx={{ color: themeColors.danger, fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.5px' }}>
                  ERROR
                </Typography>
              </Box>
              {testRunError && (
                <Box
                  onClick={(e) => { e.stopPropagation(); setErrorModalOpen(true); }}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 1,
                    p: 0.5,
                    mx: -0.5,
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                      backgroundColor: `${themeColors.danger}06`,
                    },
                  }}
                >
                  <Typography
                    level="body-xs"
                    sx={{
                      color: themeColors.danger,
                      fontSize: '0.625rem',
                      lineHeight: 1.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {testRunError}
                  </Typography>
                  <Typography
                    level="body-xs"
                    sx={{
                      color: themeColors.danger,
                      fontSize: '0.5625rem',
                      mt: 0.25,
                      fontWeight: 500,
                      opacity: 0.7,
                    }}
                  >
                    Click to expand
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Handles */}
      {config.hasTargetHandle && (
        <Handle
          type="target"
          position="left"
          isConnectable={isConnectable}
          className="modern-node-handle"
          style={{
            width: 16,
            height: 16,
            backgroundColor: 'white',
            border: `2.5px solid ${config.colors.handle}`,
            boxShadow: shadows.sm,
            left: -8,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />
      )}
      {config.hasSourceHandle && (
        <Handle
          type="source"
          position="right"
          isConnectable={isConnectable}
          className="modern-node-handle"
          style={{
            width: 16,
            height: 16,
            backgroundColor: 'white',
            border: `2.5px solid ${config.colors.handle}`,
            boxShadow: shadows.sm,
            right: -8,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />
      )}
    </Box>

    {/* Output Expansion Modal */}
    <Modal open={outputModalOpen} onClose={() => setOutputModalOpen(false)}>
      <ModalDialog
        sx={{
          width: 600,
          maxWidth: '90vw',
          maxHeight: '80vh',
          border: 'none',
          boxShadow: shadows.xl,
          borderRadius: 2.5,
          p: 0,
        }}
      >
        <ModalClose
          sx={{
            top: 12,
            right: 12,
            width: 32,
            height: 32,
            backgroundColor: themeColors.neutral[100],
            border: 'none',
            '&:hover': { backgroundColor: themeColors.neutral[200] },
          }}
        />
        <Box sx={{ p: 2.5, borderBottom: `1px solid ${themeColors.neutral[100]}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CheckCircleIcon sx={{ fontSize: 18, color: themeColors.success }} />
          <Typography level="title-md" sx={{ fontWeight: 600, color: themeColors.neutral[900] }}>
            {data.label || config.label}
          </Typography>
        </Box>
        <Box sx={{ p: 2.5, overflow: 'auto', maxHeight: '60vh' }}>
          <Typography
            level="body-sm"
            sx={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: '0.8125rem',
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: themeColors.neutral[800],
            }}
          >
            {testRunOutput}
          </Typography>
        </Box>
      </ModalDialog>
    </Modal>

    {/* Error Expansion Modal */}
    <Modal open={errorModalOpen} onClose={() => setErrorModalOpen(false)}>
      <ModalDialog
        sx={{
          width: 600,
          maxWidth: '90vw',
          maxHeight: '80vh',
          border: 'none',
          boxShadow: shadows.xl,
          borderRadius: 2.5,
          p: 0,
        }}
      >
        <ModalClose
          sx={{
            top: 12,
            right: 12,
            width: 32,
            height: 32,
            backgroundColor: themeColors.neutral[100],
            border: 'none',
            '&:hover': { backgroundColor: themeColors.neutral[200] },
          }}
        />
        <Box sx={{ p: 2.5, borderBottom: `1px solid ${themeColors.neutral[100]}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ErrorIcon sx={{ fontSize: 18, color: themeColors.danger }} />
          <Typography level="title-md" sx={{ fontWeight: 600, color: themeColors.neutral[900] }}>
            {data.label || config.label} — Error
          </Typography>
        </Box>
        <Box sx={{ p: 2.5, overflow: 'auto', maxHeight: '60vh' }}>
          <Typography
            level="body-sm"
            sx={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: '0.8125rem',
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: themeColors.danger,
            }}
          >
            {testRunError}
          </Typography>
        </Box>
      </ModalDialog>
    </Modal>
    </>
  );
}
